import React, { useEffect, useState } from 'react';
import { formatCurrency, parseDecimal, toInputValue } from '../../utils/format';
import { MIN_QUANTITY } from '../../utils/lista';

/**
 * Campo numérico que guarda o texto exato digitado.
 *
 * Sem isso, converter a cada tecla impede digitar valores decimais: "1," vira
 * 1 e o cursor perde a vírgula. Aqui o texto só é normalizado ao sair do campo.
 */
const NumericField = ({
  value,
  decimals,
  min = 0,
  onCommit,
  className,
  ariaLabel,
}) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(() => toInputValue(value, decimals));

  useEffect(() => {
    if (!focused) {
      setDraft(toInputValue(value, decimals));
    }
  }, [value, decimals, focused]);

  const handleChange = (event) => {
    const raw = event.target.value;
    setDraft(raw);

    const parsed = parseDecimal(raw);

    if (parsed !== null) {
      onCommit(parsed);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseDecimal(draft);
    const next = parsed === null ? min : parsed;

    if (next !== value) {
      onCommit(next);
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      value={draft}
      aria-label={ariaLabel}
      onChange={handleChange}
      onFocus={(event) => {
        setFocused(true);
        event.target.select();
      }}
      onBlur={handleBlur}
    />
  );
};

/**
 * Nome do item.
 *
 * É um textarea de uma linha só: assim o nome comprido aparece inteiro, em
 * várias linhas, em vez de ser cortado como acontece num input. A altura
 * acompanha o texto pelo espelho invisível no CSS (data-value), sem medir
 * nada em JavaScript.
 */
const NameField = ({ value, onCommit }) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    if (!focused) {
      setDraft(value || '');
    }
  }, [value, focused]);

  const handleChange = (event) => {
    // Continua sendo um campo de uma linha: texto colado com quebras vira
    // espaço em vez de esticar a linha do item.
    const next = event.target.value.replace(/\s*[\r\n]+\s*/g, ' ');
    setDraft(next);
    onCommit(next);
  };

  return (
    <span className="item__name-field" data-value={draft}>
      <textarea
        className="item__name"
        rows={1}
        value={draft}
        placeholder="Nome do item"
        aria-label="Nome do item"
        enterKeyHint="done"
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
      />
    </span>
  );
};

const ItemRow = ({ item, tipoDeMedida = 'unidade', onPatch, onToggle, onRemove }) => {
  const isWeight = tipoDeMedida === 'peso';
  const minQuantity = MIN_QUANTITY[tipoDeMedida] ?? 1;
  const quantidade = Number(item.quantidade) || minQuantity;
  const itemName = item.nome || 'item';

  const stepQuantity = (delta) => {
    onPatch({ quantidade: Math.max(minQuantity, quantidade + delta) });
  };

  return (
    <li className={`item${item.checked ? ' is-checked' : ''}`}>
      <label className="check">
        <input
          type="checkbox"
          checked={Boolean(item.checked)}
          onChange={onToggle}
          aria-label={`Marcar ${itemName} como comprado`}
        />
        <span className="check__mark" aria-hidden="true">
          <i className="bi bi-check-lg"></i>
        </span>
      </label>

      <NameField value={item.nome} onCommit={(nome) => onPatch({ nome })} />

      <span className="item__aside">
        <span className="item__total">{formatCurrency(item.total)}</span>
        <button
          type="button"
          className="item__remove"
          onClick={onRemove}
          aria-label={`Remover ${itemName}`}
          title="Remover item"
        >
          <i className="bi bi-trash3" aria-hidden="true"></i>
        </button>
      </span>

      <div className="item__meta">
        {isWeight ? (
          <span className="money">
            <NumericField
              value={quantidade}
              min={minQuantity}
              className="money__input"
              ariaLabel={`Peso de ${itemName} em quilos`}
              onCommit={(value) => onPatch({ quantidade: value })}
            />
            <span className="money__suffix">kg</span>
          </span>
        ) : (
          <span className="stepper">
            <button
              type="button"
              className="stepper__btn"
              onClick={() => stepQuantity(-1)}
              disabled={quantidade <= minQuantity}
              aria-label={`Diminuir quantidade de ${itemName}`}
            >
              <i className="bi bi-dash-lg" aria-hidden="true"></i>
            </button>
            <NumericField
              value={quantidade}
              min={minQuantity}
              className="stepper__input"
              ariaLabel={`Quantidade de ${itemName}`}
              onCommit={(value) => onPatch({ quantidade: value })}
            />
            <button
              type="button"
              className="stepper__btn"
              onClick={() => stepQuantity(1)}
              aria-label={`Aumentar quantidade de ${itemName}`}
            >
              <i className="bi bi-plus-lg" aria-hidden="true"></i>
            </button>
          </span>
        )}

        <span className="money">
          <span className="money__prefix" aria-hidden="true">
            R$
          </span>
          <NumericField
            value={Number(item.preco) || 0}
            decimals={2}
            min={0}
            className="money__input"
            ariaLabel={isWeight ? `Preço por quilo de ${itemName}` : `Preço de ${itemName}`}
            onCommit={(value) => onPatch({ preco: value })}
          />
          {isWeight && <span className="money__suffix">/kg</span>}
        </span>
      </div>
    </li>
  );
};

export default ItemRow;
